import { Box, chakra, Flex, HStack, Stack, Table, Text } from '@chakra-ui/react';
import { ArrowRight, ExternalLink, Loader2 } from 'lucide-react';

import type { Quake } from '../api/types';
import type { TableDensity } from '../hooks/useDashboardControls';
import { defensiveAdmit, devWarnLeakedMovers } from '../lib/defensive';
import { money, pctFromProb, pp, spreadPct } from '../lib/format';
import { polymarketEventUrlFromEventSlug } from '../lib/polymarket';
import { ScoreGauge } from './ScoreGauge';

interface LiveMoversTableProps {
  movers: Quake[];
  isLoading: boolean;
  mode?: 'compact' | 'expanded';
  onViewAllMovers?: () => void;
  onSelectMover?: (mover: Quake) => void;
  selectedMoverKey?: string | undefined;
  rowCount?: number;
  density?: TableDensity;
}

export function LiveMoversTable({
  movers,
  isLoading,
  mode = 'compact',
  onViewAllMovers,
  onSelectMover,
  selectedMoverKey,
  rowCount,
  density = 'comfortable',
}: LiveMoversTableProps) {
  const rowSlots = mode === 'expanded' ? (rowCount ?? 50) : (rowCount ?? 10);
  const rowHeight = density === 'compact' ? 52 : 64;
  const tableBodyHeight = 41 + rowSlots * rowHeight;

  // Backend owns admission into Confirmed or Watchlist. The client keeps
  // this compatibility hook while the scorer is heat-based.
  const { admitted, leaked } = defensiveAdmit(movers);
  devWarnLeakedMovers(leaked);
  const visibleMovers = admitted.slice(0, rowSlots);
  const placeholders =
    mode === 'compact' ? Array.from({ length: Math.max(0, rowSlots - visibleMovers.length) }, (_, index) => index) : [];
  const questionClamp = density === 'compact' ? 'oq-clamp-1' : 'oq-clamp-2';

  return (
    <Box as="section" layerStyle="glassPanel" borderRadius="oqPanel" overflow="hidden">
      <Flex align="center" justify="space-between" borderBottomWidth="1px" borderColor="border.subtle" px={5} py={3}>
        <HStack gap={3}>
          <Box display="grid" placeItems="center" h={6} w={6} borderRadius="oqControl" bg="brand.solid/80" fontSize="sm" fontWeight="bold" color="brand.fg">
            1
          </Box>
          <Text fontSize="lg" fontWeight="semibold" color="text.primary">
            Live Oddsquakes
          </Text>
        </HStack>
        {isLoading ? <Loader2 className="oq-spin" size={16} color="currentColor" /> : null}
      </Flex>

      <Box
        className="scrollbar-soft" overflowX="auto" overflowY="hidden"
        style={mode === 'compact' ? { height: tableBodyHeight } : undefined}
      >
        <Table.Root size="sm" variant="line" stickyHeader minW="1240px">
          <Table.Header bg="surface.panel">
            <Table.Row borderBottomWidth="1px" borderColor="border.subtle" textAlign="left" fontSize="xs" fontWeight="medium" color="text.muted">
              <Table.ColumnHeader w={12} px={5} py={3}>
                #
              </Table.ColumnHeader>
              <Table.ColumnHeader px={3} py={3}>
                Market
              </Table.ColumnHeader>
              <Table.ColumnHeader px={3} py={3}>
                Outcome
              </Table.ColumnHeader>
              <Table.ColumnHeader px={3} py={3}>
                Dir
              </Table.ColumnHeader>
              <Table.ColumnHeader px={3} py={3}>
                Price (Old → New)
              </Table.ColumnHeader>
              <Table.ColumnHeader px={3} py={3}>
                Change
              </Table.ColumnHeader>
              <Table.ColumnHeader px={3} py={3}>
                Window Vol
              </Table.ColumnHeader>
              <Table.ColumnHeader px={3} py={3}>
                Spike
              </Table.ColumnHeader>
              <Table.ColumnHeader px={3} py={3}>
                Cap Move
              </Table.ColumnHeader>
              <Table.ColumnHeader px={3} py={3}>
                Persist
              </Table.ColumnHeader>
              <Table.ColumnHeader px={3} py={3}>
                Spread
              </Table.ColumnHeader>
              <Table.ColumnHeader px={3} py={3}>
                Quake
              </Table.ColumnHeader>
              <Table.ColumnHeader px={3} py={3}>
                Signals
              </Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {visibleMovers.map((mover, index) => {
              const positive = mover.change_pp >= 0;
              const key = moverKey(mover);
              const selected = selectedMoverKey === key;
              const url = polymarketEventUrlFromEventSlug(mover.event_slug);
              return (
                <Table.Row
                  key={mover.asset_id}
                  borderBottomWidth="1px"
                  borderColor="border.subtle"
                  bg={selected ? 'brand.subtle' : undefined}
                  _hover={{ bg: 'surface.control.hovered' }}
                  transition="backgrounds"
                  style={{ height: rowHeight }}
                >
                  <Table.Cell px={5} py={2.5} color="text.muted">
                    {index + 1}
                  </Table.Cell>
                  <Table.Cell maxW="320px" px={3} py={2.5}>
                    <Flex align="flex-start" gap={2}>
                      <Box minW={0}>
                        <chakra.button
                          type="button"
                          onClick={() => onSelectMover?.(mover)}
                          textAlign="left"
                          fontWeight="medium"
                          lineHeight="snug"
                          color="text.primary"
                          transition="colors"
                          _hover={{ color: 'signal.blue' }}
                          className={questionClamp}
                        >
                          {mover.question}
                        </chakra.button>
                        <Box mt={1} fontSize="xs" color="text.muted" truncate>
                          {mover.category || 'uncategorized'}
                        </Box>
                      </Box>
                      {url ? (
                        <chakra.a
                          href={url}
                          target="_blank"
                          rel="noreferrer noopener"
                          aria-label="Open market on Polymarket"
                          mt={0.5}
                          flexShrink={0}
                          color="text.muted"
                          transition="colors"
                          _hover={{ color: 'signal.blue' }}
                        >
                          <ExternalLink size={16} />
                        </chakra.a>
                      ) : null}
                    </Flex>
                  </Table.Cell>
                  <Table.Cell px={3} py={2.5}>
                    <OutcomeBadge label={mover.outcome || (positive ? 'YES' : 'NO')} positive={positive} />
                  </Table.Cell>
                  <Table.Cell px={3} py={2.5}>
                    <DirectionBadge direction={mover.direction} />
                  </Table.Cell>
                  <Table.Cell whiteSpace="nowrap" px={3} py={2.5} fontWeight="medium" color="text.primary">
                    {pctFromProb(mover.old_price)}{' '}
                    <Box as="span" display="inline-block" mx={2} verticalAlign="baseline" w="3.5" h="3.5" color="text.muted">
                      <ArrowRight />
                    </Box> {pctFromProb(mover.new_price)}
                  </Table.Cell>
                  <Table.Cell
                    whiteSpace="nowrap"
                    px={3}
                    py={2.5}
                    fontWeight="bold"
                    color={positive ? 'signal.good' : 'signal.bad'}
                  >
                    {pp(mover.change_pp)}
                  </Table.Cell>
                  <Table.Cell whiteSpace="nowrap" px={3} py={2.5} color="text.primary">
                    {money(mover.window_volume)}
                  </Table.Cell>
                  <Table.Cell whiteSpace="nowrap" px={3} py={2.5} color="text.primary">
                    {ratio(mover.volume_spike_ratio)}
                  </Table.Cell>
                  <Table.Cell whiteSpace="nowrap" px={3} py={2.5} color="text.primary">
                    {capMove(mover.capital_weighted_move)}
                  </Table.Cell>
                  <Table.Cell whiteSpace="nowrap" px={3} py={2.5} fontWeight="semibold" color="signal.good">
                    {ratio(mover.directional_persistence)}
                  </Table.Cell>
                  <Table.Cell
                    whiteSpace="nowrap"
                    px={3}
                    py={2.5}
                    fontWeight="semibold"
                    color={mover.spread <= 0.023 ? 'signal.good' : mover.spread <= 0.035 ? 'signal.warn' : 'signal.bad'}
                  >
                    {spreadPct(mover.spread)}
                  </Table.Cell>
                  <Table.Cell px={3} py={2.5}>
                    <ScoreGauge score={mover.score} mover={mover} small />
                  </Table.Cell>
                  <Table.Cell px={3} py={2.5}>
                    <SignalBadge signal={mover.signal} />
                  </Table.Cell>
                </Table.Row>
              );
            })}
            {placeholders.map((index) => (
              <PlaceholderRow
                key={`placeholder-${index}`}
                rowNumber={visibleMovers.length + index + 1}
                message={index === 0 ? placeholderMessage(isLoading, visibleMovers.length) : ''}
                rowHeight={rowHeight}
              />
            ))}
          </Table.Body>
        </Table.Root>
      </Box>

      {mode === 'compact' ? (
        <chakra.button
          type="button"
          onClick={onViewAllMovers}
          display="flex"
          w="full"
          alignItems="center"
          justifyContent="center"
          gap={2}
          borderTopWidth="1px"
          borderColor="border.subtle"
          py={3}
          fontSize="sm"
          fontWeight="medium"
          color="text.muted"
          transition="colors"
          _hover={{ color: 'text.primary' }}
        >
          View all movers <ExternalLink size={14} />
        </chakra.button>
      ) : (
        <Flex align="center" justify="space-between" borderTopWidth="1px" borderColor="border.subtle" px={5} py={3} fontSize="sm" color="text.muted">
          <Text as="span">Showing up to {rowSlots} movers from the active filters.</Text>
          <Text as="span">{visibleMovers.length} live rows</Text>
        </Flex>
      )}
    </Box>
  );
}

function PlaceholderRow({
  rowNumber,
  message,
  rowHeight,
}: {
  rowNumber: number;
  message: string;
  rowHeight: number;
}) {
  return (
    <Table.Row borderBottomWidth="1px" borderColor="border.subtle" opacity={0.45} style={{ height: rowHeight }}>
      <Table.Cell px={5} py={2.5}>
        {rowNumber}
      </Table.Cell>
      <Table.Cell px={3} py={2.5}>
        <Text fontWeight="medium" color="text.muted">
          {message}
        </Text>
      </Table.Cell>
      <Table.Cell px={3} py={2.5}>
        -
      </Table.Cell>
      <Table.Cell px={3} py={2.5}>
        -
      </Table.Cell>
      <Table.Cell px={3} py={2.5}>
        -
      </Table.Cell>
      <Table.Cell px={3} py={2.5}>
        -
      </Table.Cell>
      <Table.Cell px={3} py={2.5}>
        -
      </Table.Cell>
      <Table.Cell px={3} py={2.5}>
        -
      </Table.Cell>
      <Table.Cell px={3} py={2.5}>
        -
      </Table.Cell>
      <Table.Cell px={3} py={2.5}>
        -
      </Table.Cell>
      <Table.Cell px={3} py={2.5}>
        -
      </Table.Cell>
      <Table.Cell px={3} py={2.5}>
        -
      </Table.Cell>
      <Table.Cell px={3} py={2.5}>
        -
      </Table.Cell>
      <Table.Cell px={3} py={2.5}>
        -
      </Table.Cell>
    </Table.Row>
  );
}

function placeholderMessage(isLoading: boolean, liveRows: number) {
  if (isLoading) return 'Loading live movers...';
  if (liveRows === 0) return 'No confirmed movers yet';
  return '';
}

export function moverKey(mover: Quake) {
  return mover.market_id || mover.asset_id;
}

function SignalBadge({ signal }: { signal: string }) {
  const normalized = signal.toLowerCase();
  const tone = normalized.includes('liquidity')
    ? 'neutral'
    : normalized.includes('timing')
      ? 'orange'
      : normalized.includes('related')
        ? 'red'
        : 'blue';
  const toneStyles: Record<string, { borderColor: string; bg: string; color: string }> = {
    red: { borderColor: 'signal.bad/30', bg: 'signal.bad/10', color: 'signal.bad' },
    orange: { borderColor: 'signal.warn/30', bg: 'signal.warn/10', color: 'signal.warn' },
    blue: { borderColor: 'brand.solid/30', bg: 'brand.subtle', color: 'brand.fg' },
    neutral: { borderColor: 'border.subtle', bg: 'surface.control', color: 'text.muted' },
  };
  const styles = toneStyles[tone] ?? toneStyles.blue!;

  return (
    <Stack
      as="span"
      direction="row"
      gap={0}
      whiteSpace="nowrap"
      borderRadius="oqControl"
      borderWidth="1px"
      borderColor={styles.borderColor}
      bg={styles.bg}
      color={styles.color}
      px={2.5}
      py={1}
      fontSize="xs"
      fontWeight="semibold"
      align="center"
    >
      {signal}
    </Stack>
  );
}

function ratio(value: number) {
  return `${value.toFixed(value >= 10 ? 0 : 2)}x`;
}

// Cap Move is the capital-weighted move component, NOT a dollar amount.
// It is window_volume * abs(change_pp), so it is a score ingredient, not
// spendable capital. The label says "Cap Move"; the value is rendered in
// pp-equivalent units to make that clear.
function capMove(value: number) {
  return `${money(value)}pp`;
}

function OutcomeBadge({ label, positive }: { label: string; positive: boolean }) {
  return (
    <Box
      as="span"
      title={label}
      display="inline-block"
      maxW="8rem"
      truncate
      whiteSpace="nowrap"
      borderRadius="oqControl"
      px={2.5}
      py={1}
      verticalAlign="middle"
      fontSize="xs"
      fontWeight="black"
      textTransform="uppercase"
      letterSpacing="wide"
      bg={positive ? 'signal.good/12' : 'signal.bad/12'}
      color={positive ? 'signal.good' : 'signal.bad'}
    >
      {label}
    </Box>
  );
}

// Direction is up/down/flat, the price-move direction. Distinct from
// Outcome, which is YES/NO/UNDER/etc.
function DirectionBadge({ direction }: { direction: string }) {
  const value = direction.toLowerCase();
  if (value === 'up') {
    return (
      <Stack
        as="span"
        direction="row"
        align="center"
        gap={1}
        display="inline-flex"
        borderRadius="oqControl"
        bg="signal.good/10"
        px={2}
        py={1}
        fontSize="xs"
        fontWeight="semibold"
        color="signal.good"
      >
        <span aria-hidden>▲</span>UP
      </Stack>
    );
  }
  if (value === 'down') {
    return (
      <Stack
        as="span"
        direction="row"
        align="center"
        gap={1}
        display="inline-flex"
        borderRadius="oqControl"
        bg="signal.bad/10"
        px={2}
        py={1}
        fontSize="xs"
        fontWeight="semibold"
        color="signal.bad"
      >
        <span aria-hidden>▼</span>DOWN
      </Stack>
    );
  }
  return (
    <Stack
      as="span"
      direction="row"
      align="center"
      gap={1}
      display="inline-flex"
      borderRadius="oqControl"
      borderWidth="1px"
      borderColor="border.subtle"
      bg="surface.control.hovered"
      px={2}
      py={1}
      fontSize="xs"
      fontWeight="semibold"
      color="text.muted"
    >
      FLAT
    </Stack>
  );
}